import React from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Box, Fab, Fade, ListItemIcon, Menu, MenuItem, useTheme } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { type BaseProps } from '../../hooks/useTabs';
import useSettings from '../../hooks/useSettings';
import useUserData from '../../hooks/useUserData';
import useIsModuleDisabled from '../../hooks/useIsModuleDisabled';
import Icon from '../../Icon';

export type FabOptionType<T> = {
	name: string;
	icon: string;
	hidden?: (fabProps: T & BaseProps) => boolean;
	disabled?: (fabProps: T & BaseProps) => boolean;
	action?: (fabProps: T & BaseProps) => void;
};

export type FabType<T> = {
	id: string;
	iconAdd?: boolean;
	options?: FabOptionType<T>[];
} & FabOptionType<T>;

interface PageFabProps<T> {
	fab: FabType<T>;
	fabProps: T;
	currentTabId?: string;
}

export default function PageFab<T>(props: PageFabProps<T>) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const userData = useUserData();
	const intl = useIntl();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);
	const theme = useTheme();

	const fabArgs = { ...props.fabProps, dispatch, sysSettings, isModuleDisabled, authorizer, userData, intl, t };

	const ref = React.useRef<HTMLDivElement>(undefined);
	const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement>();
	const isMenuOpen = Boolean(anchorEl);
	const showMenu = (props.fab.options?.length || 0) > 0;

	const options = props.fab.options?.filter(option => !option.hidden || !option.hidden(fabArgs));

	const openMenu = () => {
		setAnchorEl(ref.current);
	};

	const closeMenu = () => {
		setAnchorEl(undefined);
	};

	const transitionDuration = {
		enter: theme.transitions.duration.enteringScreen,
		exit: theme.transitions.duration.leavingScreen
	};

	return (
		<Fade in timeout={transitionDuration} style={{ transitionDelay: `${transitionDuration.exit}ms` }} unmountOnExit>
			<Box ref={ref}>
				{!isMenuOpen && (
					<Fab
						className="m-4"
						variant="extended"
						color="secondary"
						onClick={() => (showMenu ? openMenu() : props.fab.action?.(fabArgs))}
					>
						{props.fab.iconAdd && <Icon icon="plus" size={14} />}
						<Icon className="mr-4" icon={props.fab.icon} />
						{t(props.fab.name)}
					</Fab>
				)}
				{showMenu && (
					<Menu
						anchorEl={anchorEl}
						open={isMenuOpen}
						onClose={closeMenu}
						transformOrigin={{ horizontal: 'right', vertical: 'top' }}
						anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
						slotProps={{ paper: { className: 'min-w-200' } }}
					>
						{options?.map((option, index) => (
							<MenuItem
								key={index}
								disabled={option.disabled && option.disabled(fabArgs)}
								onClick={() => {
									option.action?.(fabArgs);
									closeMenu();
								}}
							>
								<ListItemIcon>
									<Icon icon={option.icon} />
								</ListItemIcon>
								{t(option.name)}
							</MenuItem>
						))}
					</Menu>
				)}
			</Box>
		</Fade>
	);
}
